import clsx from 'clsx';
import React, { CSSProperties, ReactNode, useLayoutEffect, useRef, useState } from 'react';

import HighlightSearchTerm from '@/src/components/project/Common/Typography/HighlightSearchTerm';

import MaterialUITooltip from '../Tooltip/MaterialUITooltip/MaterialUITooltip';

type LongTextByWidthProps = {
  value: string;
  id?: string;
  maxWidth: number;
  maxLines?: number;
  className?: string;
  hasTooltip?: boolean;
  tooltipClassName?: string;
  children?: ReactNode;
  highlightBySearchTerm?: boolean;
  searchTerm?: string;
};

const LongTextByWidth = ({
  value,
  id,
  maxWidth,
  maxLines = 1,
  className = '',
  hasTooltip = true,
  tooltipClassName = '',
  children,
  highlightBySearchTerm = false,
  searchTerm = '',
}: LongTextByWidthProps) => {
  const textRef = useRef<HTMLParagraphElement>();
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const tooltipId = `ltbw_${id}`;

  useLayoutEffect(() => {
    if (textRef.current) {
      const element = textRef.current;
      if (maxLines === 1) {
        const isEllipsis = element.scrollWidth > element.clientWidth;

        setIsEllipsisActive(isEllipsis);
      } else {
        // Set in useEffect to reliablly compute state of ellipsis for webkitLineClamp implementation
        const originalHeight = element.getBoundingClientRect().height;

        element.style.webkitLineClamp = `${maxLines}`;

        const newHeight = element.getBoundingClientRect().height;

        if (newHeight < originalHeight) {
          setIsEllipsisActive(true);
        } else {
          setIsEllipsisActive(false);
        }
      }
    }
  }, [maxWidth, maxLines]);

  const textStyles: CSSProperties =
    maxLines === 1
      ? {
        maxWidth,
        width: 'auto',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }
      : {
        maxWidth,
        WebkitBoxOrient: 'vertical',
        display: '-webkit-box',
      };

  if (isEllipsisActive && hasTooltip) {
    return (
      <MaterialUITooltip
        id={tooltipId}
        classes={{ tooltip: tooltipClassName }}
        title={value}
        TransitionProps={{ timeout: 0 }}
      >
        <p
          ref={textRef}
          style={textStyles}
          className={clsx(
            'p--longText',
            'tw-leading-[14px] tw-whitespace-normal tw-break-words',
            'tw-w-full tw-overflow-hidden',
            className
          )}
          data-tooltip-id={tooltipId}
        >
          {highlightBySearchTerm ? <HighlightSearchTerm customSearchTerm={searchTerm}>{value}</HighlightSearchTerm> : value}
        </p>
      </MaterialUITooltip>
    );
  }

  return (
    <>
      <p
        ref={textRef}
        style={textStyles}
        className={clsx(
          'p--longText',
          'tw-leading-[14px] tw-whitespace-normal tw-break-words',
          'tw-w-full tw-overflow-hidden',
          className
        )}
        data-tooltip-id={tooltipId}
      >
        {highlightBySearchTerm ? <HighlightSearchTerm customSearchTerm={searchTerm}>{value}</HighlightSearchTerm> : value}
      </p>
      {children}
    </>
  );
};

export default LongTextByWidth;
